import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import Title from '@/components/ui/Title';
import { Check, Loader2, Plus, Trash2, X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import { Skeleton } from '@/components/ui/skeleton';
import type { QuizAnswerOption, QuizQuestion, QuizTemplate } from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { useAppSelector } from '@/app/hooks';
import { webinarService } from '@/services/webinar.service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { normalizeAxiosError, AppError } from '@/http/errors';
import { mapHttpError } from '@/lib/mapApiError';
import PreviewActivityQuiz from './PreviewActivityQuiz';

const makeAnswerSchema = (t: TFunction) =>
  z.object({
    id: z.number().optional(),
    textContent: z.string().min(1, t('waitingRoom.quiz.validation.answerRequired')),
    position: z.number().int().nonnegative().optional().nullable(),
    correct: z.boolean(),
  });

const makeQuestionSchema = (t: TFunction) =>
  z
    .object({
      id: z.number().optional(),
      textContent: z.string().min(1, t('waitingRoom.quiz.validation.questionRequired')),
      type: z.enum(['SINGLE_CHOICE']),
      position: z.number().int().nonnegative().optional().nullable(),
      options: z
        .array(makeAnswerSchema(t))
        .min(2, t('waitingRoom.quiz.validation.minAnswersRequired'))
        .max(6, t('waitingRoom.quiz.validation.maxAnswersExceeded')),
    })
    .superRefine(({ options }, ctx) => {
      const correctAnswersCount = options.filter((answer) => answer.correct).length;
      if (correctAnswersCount !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('waitingRoom.quiz.validation.exactlyOneCorrectAnswer'),
          path: ['options'],
        });
      }
    });

const makeQuizSchema = (t: TFunction) =>
  z.object({
    name: z.string().min(1, t('waitingRoom.quiz.validation.titleRequired')),
    subtitle: z.string().min(1, t('waitingRoom.quiz.validation.subtitleRequired')),
    questions: z
      .array(makeQuestionSchema(t))
      .min(1, t('waitingRoom.quiz.validation.minQuestionsRequired')),
  });

interface QuizFormWaitingRoomProps {
  webinarId: number;
  workspaceId: number;
  visualIdentity: WorkspaceVisualIdentity;
  showQuiz: boolean;
  onFormReady?: (form: UseFormReturn<z.infer<ReturnType<typeof makeQuizSchema>>>) => void;
  isEdit?: boolean;
  isEvergreen?: boolean;
}

export default function QuizFormWaitingRoom({
  webinarId,
  workspaceId,
  visualIdentity,
  showQuiz,
  onFormReady,
  isEvergreen,
}: QuizFormWaitingRoomProps) {
  const { t } = useTranslation('webinars');
  const { t: tCW } = useTranslation('createWebinar');
  const currentWaitingRoomPage = useAppSelector((state) => state.webinar.currentWaitingRoomPage);
  const [isLoading, setIsLoading] = useState(true);
  const [existingQuizTemplate, setExistingQuizTemplate] = useState<QuizTemplate | null>(null);
  const [accordionValue, setAccordionValue] = useState<string>(isEvergreen ? '' : 'item-1');
  const [deletingQuestionIndex, setDeletingQuestionIndex] = useState<number | null>(null);
  const [deletingAnswerKey, setDeletingAnswerKey] = useState<string | null>(null);

  const quizSchema = useMemo(() => makeQuizSchema(t), [t]);
  const form = useForm<z.infer<ReturnType<typeof makeQuizSchema>>>({
    resolver: zodResolver(quizSchema),
    defaultValues: {
      name: '',
      subtitle: '',
      questions: [
        {
          textContent: '',
          type: 'SINGLE_CHOICE',
          options: [
            {
              textContent: '',
              correct: true,
            },
            {
              textContent: '',
              correct: false,
            },
          ],
        },
      ],
    },
    mode: 'onChange',
  });

  const toggleCorrectAnswer = (questionIndex: number, answerIndex: number) => {
    const currentAnswers = form.getValues(`questions.${questionIndex}.options`);
    const questionType = form.getValues(`questions.${questionIndex}.type`);

    const correctAnswersCount = currentAnswers.filter((answer) => answer.correct).length;

    if (questionType === 'SINGLE_CHOICE') {
      currentAnswers.forEach((_, index) => {
        form.setValue(
          `questions.${questionIndex}.options.${index}.correct`,
          index === answerIndex,
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
      });
      return;
    }

    if (currentAnswers[answerIndex].correct && correctAnswersCount > 1) {
      form.setValue(`questions.${questionIndex}.options.${answerIndex}.correct`, false, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else if (!currentAnswers[answerIndex].correct) {
      form.setValue(`questions.${questionIndex}.options.${answerIndex}.correct`, true, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const removeAnswer = async (questionIndex: number, answerIndex: number) => {
    const currentOptions = form.getValues(`questions.${questionIndex}.options`);
    if (currentOptions.length <= 2) {
      return;
    }

    const optionToRemove = currentOptions[answerIndex];
    const key = `${questionIndex}-${answerIndex}`;

    if (optionToRemove.id && optionToRemove.id > 0 && existingQuizTemplate) {
      const questionId = form.getValues(`questions.${questionIndex}.id`);
      if (questionId) {
        setDeletingAnswerKey(key);
        try {
          await webinarService.deleteQuizAnswerOption(
            workspaceId,
            webinarId,
            existingQuizTemplate.id,
            questionId,
            optionToRemove.id,
          );
        } catch {
          toast.error(t('errors.generic'));
          setDeletingAnswerKey(null);
          return;
        }
        setDeletingAnswerKey(null);
      }
    }

    const newAnswers = currentOptions.filter((_, index) => index !== answerIndex);
    form.setValue(`questions.${questionIndex}.options`, newAnswers, {
      shouldDirty: false,
      shouldValidate: true,
    });
  };

  const addAnswer = (questionIndex: number) => {
    const currentOptions = form.getValues(`questions.${questionIndex}.options`);
    if (currentOptions.length < 6) {
      const nextPosition = currentOptions.length;
      form.setValue(`questions.${questionIndex}.options`, [
        ...currentOptions,
        { textContent: '', correct: false, position: nextPosition },
      ]);
    }
  };

  const addQuestion = () => {
    const currentQuestions = form.getValues('questions');
    form.setValue('questions', [
      ...currentQuestions,
      {
        textContent: '',
        type: 'SINGLE_CHOICE',
        options: [
          {
            textContent: '',
            correct: true,
          },
          {
            textContent: '',
            correct: false,
          },
        ],
      },
    ]);
  };

  const removeQuestion = async (questionIndex: number) => {
    const currentQuestions = form.getValues('questions');
    if (currentQuestions.length <= 1) {
      return;
    }

    const questionToRemove = currentQuestions[questionIndex];

    if (questionToRemove.id && questionToRemove.id > 0 && existingQuizTemplate) {
      setDeletingQuestionIndex(questionIndex);
      try {
        const existingOptions = await webinarService.listQuizAnswerOptions(
          workspaceId,
          webinarId,
          existingQuizTemplate.id,
          questionToRemove.id,
        );

        for (const option of existingOptions) {
          await webinarService.deleteQuizAnswerOption(
            workspaceId,
            webinarId,
            existingQuizTemplate.id,
            questionToRemove.id,
            option.id,
          );
        }

        await webinarService.deleteQuizQuestion(
          workspaceId,
          webinarId,
          existingQuizTemplate.id,
          questionToRemove.id,
        );
      } catch {
        toast.error(t('errors.generic'));
        setDeletingQuestionIndex(null);
        return;
      }
      setDeletingQuestionIndex(null);
    }

    const newQuestions = currentQuestions.filter((_, index) => index !== questionIndex);
    form.setValue('questions', newQuestions, {
      shouldDirty: false,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (onFormReady) {
      onFormReady(form);
    }
  }, [form, onFormReady]);

  useEffect(() => {
    if (isEvergreen) {
      setAccordionValue('');
    } else if (showQuiz) {
      setAccordionValue('item-1');
    } else {
      setAccordionValue('');
    }
  }, [showQuiz, isEvergreen]);

  const fetchAndPopulateForm = useCallback(async () => {
    if (!webinarId || !workspaceId) return;

    try {
      const templatesPage = await webinarService.listQuizTemplates(workspaceId, webinarId, 0, 50);

      const waitingRoomTemplate = templatesPage.content.find(
        (template) => template.waitingRoomQuiz === true,
      );

      if (waitingRoomTemplate) {
        setExistingQuizTemplate(waitingRoomTemplate);

        const questions = await webinarService.listQuizQuestions(
          workspaceId,
          webinarId,
          waitingRoomTemplate.id,
        );

        const sortedQuestions = [...questions].sort((a, b) => a.position - b.position);

        const formQuestions = await Promise.all(
          sortedQuestions.map(async (question) => {
            const options = await webinarService.listQuizAnswerOptions(
              workspaceId,
              webinarId,
              waitingRoomTemplate.id,
              question.id,
            );

            const sortedOptions = [...options].sort(
              (a, b) => (a.position || 0) - (b.position || 0),
            );

            return {
              id: question.id,
              textContent: question.textContent || '',
              type: question.type as 'SINGLE_CHOICE',
              position: question.position,
              options: sortedOptions.map((option) => ({
                id: option.id,
                textContent: option.textContent || '',
                correct: option.correct,
                position: option.position,
              })),
            };
          }),
        );

        const formData = {
          name: waitingRoomTemplate.name || '',
          subtitle: waitingRoomTemplate.subTitle || '',
          questions:
            formQuestions.length > 0
              ? formQuestions
              : [
                  {
                    textContent: '',
                    type: 'SINGLE_CHOICE' as const,
                    options: [
                      { textContent: '', correct: true },
                      { textContent: '', correct: false },
                    ],
                  },
                ],
        };

        form.reset(formData);
      }
    } catch (error) {
      console.error("Erreur lors du fetch du quiz de salle d'attente:", error);
    }
  }, [webinarId, workspaceId, form]);

  useEffect(() => {
    setIsLoading(true);
    void fetchAndPopulateForm().finally(() => setIsLoading(false));
  }, [fetchAndPopulateForm]);

  const onSubmit = async (data: z.infer<ReturnType<typeof makeQuizSchema>>) => {
    if (!webinarId || !workspaceId) {
      console.error("Impossible de sauvegarder le quiz de salle d'attente : webinar manquant");
      return;
    }

    try {
      if (!data.questions || data.questions.length === 0) {
        console.error("Aucune question à sauvegarder pour le quiz de salle d'attente");
        return;
      }

      const templateInput = {
        name: data.name.trim(),
        subTitle: data.subtitle.trim(),
        enabledDuringLive: false,
        shuffleQuestions: false,
        shuffleOptions: false,
        passingScore: null,
        visibleFrom: null,
        visibleUntil: null,
        published: false,
        borderColor: '#000000',
        accentColor: '#000000',
        backgroundColor: '#000000',
        titleColor: '#000000',
        waitingRoomQuiz: true,
        displayWaitingRoomQuiz: showQuiz,
      };

      const savedQuestions: QuizQuestion[] = [];

      let templateId: number;

      if (existingQuizTemplate) {
        templateId = existingQuizTemplate.id;

        templateInput.published = existingQuizTemplate.published;
        templateInput.displayWaitingRoomQuiz = showQuiz;
        await webinarService.updateQuizTemplate(workspaceId, webinarId, templateId, templateInput);

        const currentQuestions = await webinarService.listQuizQuestions(
          workspaceId,
          webinarId,
          templateId,
        );

        const existingQuestionsMap = new Map<number, (typeof currentQuestions)[0]>();
        data.questions.forEach((q) => {
          if (q.id && q.id > 0) {
            const existingQuestion = currentQuestions.find((cq) => cq.id === q.id);
            if (existingQuestion) {
              existingQuestionsMap.set(q.id, existingQuestion);
            }
          }
        });

        const processedQuestionIds: number[] = [];

        for (const [questionIndex, formQuestion] of data.questions.entries()) {
          let questionId: number;
          const savedOptions: QuizAnswerOption[] = [];

          if (formQuestion.id && formQuestion.id > 0 && existingQuestionsMap.has(formQuestion.id)) {
            const existingQuestion = existingQuestionsMap.get(formQuestion.id)!;
            questionId = existingQuestion.id;

            await webinarService.updateQuizQuestion(
              workspaceId,
              webinarId,
              templateId,
              questionId,
              {
                textContent: formQuestion.textContent.trim(),
                type: formQuestion.type,
                position: questionIndex,
                points: null,
                timeLimitSeconds: null,
              },
            );

            const existingOptions = await webinarService.listQuizAnswerOptions(
              workspaceId,
              webinarId,
              templateId,
              questionId,
            );

            const existingOptionsMap = new Map<number, (typeof existingOptions)[0]>();
            formQuestion.options.forEach((opt) => {
              if (opt.id && opt.id > 0) {
                const existingOption = existingOptions.find((eo) => eo.id === opt.id);
                if (existingOption) {
                  existingOptionsMap.set(opt.id, existingOption);
                }
              }
            });

            const processedOptionIds: number[] = [];

            for (const [optionIndex, formOption] of formQuestion.options.entries()) {
              let optionId: number;

              if (formOption.id && formOption.id > 0 && existingOptionsMap.has(formOption.id)) {
                optionId = formOption.id;

                await webinarService.updateQuizAnswerOption(
                  workspaceId,
                  webinarId,
                  templateId,
                  questionId,
                  optionId,
                  {
                    textContent: formOption.textContent.trim(),
                    correct: formOption.correct,
                    position: optionIndex,
                  },
                );
              } else {
                const newOptionId = await webinarService.createQuizAnswerOption(
                  workspaceId,
                  webinarId,
                  templateId,
                  questionId,
                  {
                    textContent: formOption.textContent.trim(),
                    correct: formOption.correct,
                    position: optionIndex,
                  },
                );

                if (!newOptionId) {
                  throw new Error('Failed to create quiz answer option: no ID returned');
                }

                optionId = newOptionId;
              }

              processedOptionIds.push(optionId);
              savedOptions.push({
                id: optionId,
                textContent: formOption.textContent.trim(),
                correct: formOption.correct,
                position: optionIndex,
              });
            }

            const optionsToDelete = existingOptions.filter(
              (opt) => !processedOptionIds.includes(opt.id),
            );

            for (const optionToDelete of optionsToDelete) {
              await webinarService.deleteQuizAnswerOption(
                workspaceId,
                webinarId,
                templateId,
                questionId,
                optionToDelete.id,
              );
            }
          } else {
            const newQuestionId = await webinarService.createQuizQuestion(
              workspaceId,
              webinarId,
              templateId,
              {
                textContent: formQuestion.textContent.trim(),
                type: formQuestion.type,
                position: questionIndex,
                points: null,
                timeLimitSeconds: null,
              },
            );

            if (!newQuestionId) {
              throw new Error('Failed to create quiz question: no ID returned');
            }

            questionId = newQuestionId;

            for (const [optionIndex, formOption] of formQuestion.options.entries()) {
              const newOptionId = await webinarService.createQuizAnswerOption(
                workspaceId,
                webinarId,
                templateId,
                questionId,
                {
                  textContent: formOption.textContent.trim(),
                  correct: formOption.correct,
                  position: optionIndex,
                },
              );

              if (!newOptionId) {
                throw new Error('Failed to create quiz answer option: no ID returned');
              }

              savedOptions.push({
                id: newOptionId,
                textContent: formOption.textContent.trim(),
                correct: formOption.correct,
                position: optionIndex,
              });
            }
          }

          processedQuestionIds.push(questionId);
          savedQuestions.push({
            id: questionId,
            textContent: formQuestion.textContent.trim(),
            type: formQuestion.type,
            position: questionIndex,
            points: 0,
            timeLimitSeconds: null,
            options: savedOptions,
          });
        }

        const questionsToDelete = currentQuestions.filter(
          (q) => !processedQuestionIds.includes(q.id),
        );

        for (const questionToDelete of questionsToDelete) {
          const optionsToDelete = await webinarService.listQuizAnswerOptions(
            workspaceId,
            webinarId,
            templateId,
            questionToDelete.id,
          );

          for (const optionToDelete of optionsToDelete) {
            await webinarService.deleteQuizAnswerOption(
              workspaceId,
              webinarId,
              templateId,
              questionToDelete.id,
              optionToDelete.id,
            );
          }

          await webinarService.deleteQuizQuestion(
            workspaceId,
            webinarId,
            templateId,
            questionToDelete.id,
          );
        }
      } else {
        const newTemplateId = await webinarService.createQuizTemplate(
          workspaceId,
          webinarId,
          templateInput,
        );

        if (!newTemplateId) {
          toast.error(t('errors.generic'));
          return;
        }

        templateId = newTemplateId;

        for (const [questionIndex, formQuestion] of data.questions.entries()) {
          const questionId = await webinarService.createQuizQuestion(
            workspaceId,
            webinarId,
            templateId,
            {
              textContent: formQuestion.textContent.trim(),
              type: formQuestion.type,
              position: questionIndex,
              points: null,
              timeLimitSeconds: null,
            },
          );

          if (!questionId) {
            throw new Error('Failed to create quiz question: no ID returned');
          }

          const savedOptions: QuizAnswerOption[] = [];
          for (const [optionIndex, formOption] of formQuestion.options.entries()) {
            const optionId = await webinarService.createQuizAnswerOption(
              workspaceId,
              webinarId,
              templateId,
              questionId,
              {
                textContent: formOption.textContent.trim(),
                correct: formOption.correct,
                position: optionIndex,
              },
            );

            if (!optionId) {
              throw new Error('Failed to create quiz answer option: no ID returned');
            }

            savedOptions.push({
              id: optionId,
              textContent: formOption.textContent.trim(),
              correct: formOption.correct,
              position: optionIndex,
            });
          }

          savedQuestions.push({
            id: questionId,
            textContent: formQuestion.textContent.trim(),
            type: formQuestion.type,
            position: questionIndex,
            points: 0,
            timeLimitSeconds: null,
            options: savedOptions,
          });
        }

        // Met à jour le template en state après création complète
        setExistingQuizTemplate({
          ...templateInput,
          id: templateId,
          webinarId,
          workspaceId,
          published: false,
          subTitle: templateInput.subTitle,
          questions: savedQuestions,
        });
      }

      form.reset({
        name: data.name,
        subtitle: data.subtitle,
        questions: savedQuestions.map((question) => ({
          id: question.id,
          textContent: question.textContent,
          type: 'SINGLE_CHOICE' as const,
          position: question.position,
          options: question.options.map((option) => ({
            id: option.id,
            textContent: option.textContent,
            correct: option.correct,
            position: option.position,
          })),
        })),
      });
      toast.success(t('waitingRoom.quiz.saveSuccess'));
      setAccordionValue('');
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du quiz de salle d'attente", error);

      const appError = error instanceof AppError ? error : normalizeAxiosError(error);
      const details = appError?.details && Array.isArray(appError.details) ? appError.details : [];

      if (details.length > 0) {
        details.forEach((detail: { message?: string; fieldName?: string }) => {
          if (detail.message) {
            const mappedMessage = mapHttpError(detail.message);
            const translatedMessage = t(mappedMessage, detail.message);
            toast.error(translatedMessage);
          }
        });
      } else if (appError?.message) {
        const mappedMessage = mapHttpError(appError.message);
        const translatedMessage = t(mappedMessage, appError.message);
        toast.error(translatedMessage);
      } else {
        toast.error(t('errors.generic'));
      }
    }
  };

  if (isLoading) {
    return (
      <div>
        <Accordion
          type="single"
          collapsible
          defaultValue={isEvergreen ? undefined : 'item-1'}
          className="space-y-[10px] rounded-[20px]"
        >
          <AccordionItem value="item-1" className="bg-medium p-6 rounded-[20px]">
            <AccordionTrigger className=" rounded-[20px] p-0">
              <Title level={2}>{t('waitingRoom.quiz.sectionTitle')}</Title>
            </AccordionTrigger>
            <AccordionContent className=" flex flex-col gap-4 rounded-[20px] bg-medium  pt-4 px-0">
              <div className="space-y-4">
                <Skeleton className="h-10 w-2/3" />
                <Skeleton className="h-10 w-2/3" />
                <Skeleton className="h-28 w-full rounded-[20px]" />
                <Skeleton className="h-28 w-full rounded-[20px]" />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }

  const hasQuizWaitingRoom = !!existingQuizTemplate;

  return (
    <div className="bg-medium rounded-[20px] p-0">
      <Accordion
        key={`quiz-accordion-${showQuiz}`}
        type="single"
        value={accordionValue}
        onValueChange={(value) => {
          setAccordionValue(value);
        }}
        collapsible
        className="space-y-[10px] rounded-[20px]"
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 rounded-[20px]"
          >
            <AccordionItem value="item-1" className="bg-medium p-6 rounded-[20px]">
              <div className="flex flex-col gap-4">
                <AccordionTrigger className="rounded-[20px] p-0" fillForm={hasQuizWaitingRoom}>
                  <Title level={2}>{t('waitingRoom.quiz.sectionTitle')}</Title>
                </AccordionTrigger>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <TextFieldWithLimit
                          label={t('waitingRoom.quiz.nameLabel')}
                          className="bg-white"
                          value={field.value ?? ''}
                          onChange={(v) => field.onChange(v)}
                          maxLength={100}
                          error={fieldState.error?.message}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="subtitle"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <TextFieldWithLimit
                          label={t('waitingRoom.quiz.subtitleLabel')}
                          className="bg-white"
                          value={field.value ?? ''}
                          onChange={(v) => field.onChange(v)}
                          maxLength={200}
                          error={fieldState.error?.message}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <AccordionContent className=" flex flex-col gap-4 rounded-[20px] bg-medium pt-4 px-0 pb-0">
                <div className="flex flex-col gap-6">
                  {form.watch('questions')?.map((_, questionIndex) => (
                    <div key={questionIndex} className="flex flex-col gap-0">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <FormField
                            name={`questions.${questionIndex}.textContent`}
                            control={form.control}
                            render={({ field, fieldState }) => (
                              <FormItem>
                                <TextFieldWithLimit
                                  label={`${tCW('quizzes.questionLabel')} ${questionIndex + 1}`}
                                  value={field.value ?? ''}
                                  onChange={(v) => field.onChange(v)}
                                  maxLength={300}
                                  error={fieldState.error?.message}
                                />
                              </FormItem>
                            )}
                          />
                        </div>
                        {form.watch('questions')?.length > 1 && (
                          <button
                            aria-label={t('waitingRoom.quiz.removeQuestion')}
                            type="button"
                            onClick={() => removeQuestion(questionIndex)}
                            disabled={deletingQuestionIndex === questionIndex}
                            className="bg-white rounded-lg h-14 px-2 flex items-center justify-center border border-gray-200 cursor-pointer disabled:opacity-50"
                          >
                            {deletingQuestionIndex === questionIndex ? (
                              <Loader2 className="size-6 animate-spin" />
                            ) : (
                              <Trash2 className="size-6" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Section Réponses */}
                      <div className="flex flex-col gap-2 pt-4">
                        <div className="space-y-4">
                          {form
                            .watch(`questions.${questionIndex}.options`)
                            ?.map((_, answerIndex) => (
                              <div key={answerIndex} className="flex flex-col">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <FormField
                                      name={`questions.${questionIndex}.options.${answerIndex}.textContent`}
                                      control={form.control}
                                      render={({ field, fieldState }) => (
                                        <FormItem>
                                          <TextFieldWithLimit
                                            label={tCW('quizzes.answerLabel', {
                                              index: answerIndex + 1,
                                            })}
                                            value={field.value ?? ''}
                                            onChange={(v) => field.onChange(v)}
                                            maxLength={300}
                                            error={fieldState.error?.message}
                                          />
                                        </FormItem>
                                      )}
                                    />
                                  </div>

                                  {/* Bouton pour marquer comme correcte */}
                                  <button
                                    aria-label={t('waitingRoom.quiz.toggleCorrectAnswer')}
                                    type="button"
                                    onClick={() => toggleCorrectAnswer(questionIndex, answerIndex)}
                                    className={`flex items-center justify-center w-10 h-14 rounded-lg border transition-colors cursor-pointer ${
                                      form.watch(
                                        `questions.${questionIndex}.options.${answerIndex}.correct`,
                                      )
                                        ? 'bg-green-100 border-input text-green-600'
                                        : 'bg-red-100 border-input text-red-600 hover:bg-red-200'
                                    }`}
                                  >
                                    {form.watch(
                                      `questions.${questionIndex}.options.${answerIndex}.correct`,
                                    ) ? (
                                      <Check className="size-6 text-green-500" />
                                    ) : (
                                      <X className="size-6 text-red-500" />
                                    )}
                                  </button>

                                  {form.watch(`questions.${questionIndex}.options`)?.length > 2 && (
                                    <button
                                      aria-label={t('waitingRoom.quiz.removeAnswer')}
                                      type="button"
                                      onClick={() => removeAnswer(questionIndex, answerIndex)}
                                      disabled={
                                        deletingAnswerKey === `${questionIndex}-${answerIndex}`
                                      }
                                      className="bg-white rounded-lg h-14 px-2 flex items-center justify-center border border-gray-200 cursor-pointer disabled:opacity-50"
                                    >
                                      {deletingAnswerKey === `${questionIndex}-${answerIndex}` ? (
                                        <Loader2 className="size-6 animate-spin" />
                                      ) : (
                                        <Trash2 className="size-6" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>

                        {form.watch(`questions.${questionIndex}.options`)?.length < 6 && (
                          <button
                            type="button"
                            onClick={() => addAnswer(questionIndex)}
                            className="flex items-center gap-2 pt-4"
                          >
                            <Plus className="size-6" />
                            <Label className="cursor-pointer text-sm">
                              {tCW('quizzes.addAnswer')}
                            </Label>
                          </button>
                        )}

                        {form.formState.errors.questions?.[questionIndex]?.options && (
                          <div className="text-left text-red-500 font-medium ml-0">
                            {form.formState.errors.questions[questionIndex].options?.root
                              ?.message ||
                              form.formState.errors.questions[questionIndex].options?.message}
                          </div>
                        )}
                        <Separator className="w-full mt-4" />
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => addQuestion()}
                    className="flex items-center gap-2 "
                  >
                    <Plus className="size-6" />
                    <Label className="cursor-pointer text-base">
                      {t('waitingRoom.quiz.addQuestion')}
                    </Label>
                  </button>

                  <Button
                    type="submit"
                    disabled={
                      form.formState.isSubmitting ||
                      !form.formState.isValid ||
                      !form.formState.isDirty
                    }
                    className=" disabled:bg-gray-200"
                  >
                    {form.formState.isSubmitting && <Loader />}
                    {tCW('save')}
                  </Button>
                </div>
                <Accordion
                  type="single"
                  defaultValue={isEvergreen ? undefined : 'preview-1'}
                  collapsible
                  className=""
                >
                  <AccordionItem value="preview-1">
                    <AccordionTrigger className=" rounded-[20px] px-0">
                      <Title level={2}>{tCW('quizzes.previewTitle')}</Title>
                    </AccordionTrigger>

                    <AccordionContent className="flex py-0  flex-col gap-4 rounded-[20px] pt-4 px-0">
                      <PreviewActivityQuiz
                        form={form}
                        visualIdentity={visualIdentity}
                        currentWaitingRoomPage={currentWaitingRoomPage}
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          </form>
        </Form>
      </Accordion>
    </div>
  );
}
